import { useCallback, useEffect, useState } from "react";
import { api, idr } from "../../lib/api";
import { toast } from "sonner";
import { useParams, useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, ArrowLeftRight, Scale, ChevronLeft, Check, Receipt, Settings2, Tag } from "lucide-react";
import InputRupiah from "../../components/InputRupiah";
import { DatePicker } from "../../components/ui/date-picker";
import { hariIniISO } from "../../lib/tanggal";
import { useAuth } from "../../context/AuthContext";
import KategoriManager from "./KategoriManager";

/* Jenis yang akunnya ditentukan lewat peta kategori DAN boleh menyebut supplier.
 * Cerminan `kategori.py::JENIS_DENGAN_SUPPLIER` — transfer & cash opname tidak
 * lewat peta kategori sama sekali. */
const JENIS_PAKAI_KATEGORI = ["uang_masuk", "uang_keluar"];

/* Warna badge sumber keputusan. LABEL-nya sengaja TIDAK ditulis ulang di sini —
 * backend yang mengirim `sumber_label`, supaya layar dan mesin tidak bisa
 * berbeda cerita (duplikasi label pernah melahirkan penyimpangan di repo ini). */
const WARNA_SUMBER = {
  map_kategori_supplier: "bg-emerald-50 text-emerald-700 border-emerald-200",
  map_kategori: "bg-sky-50 text-sky-700 border-sky-200",
  bawaan: "bg-slate-50 text-slate-600 border-slate-200",
  tidak_ada: "bg-amber-50 text-amber-700 border-amber-200",
};

/* Ringkasan "uang ini mendarat di akun mana, dan KENAPA". Dipakai di langkah
 * kategori maupun di layar konfirmasi supaya pemetaan bertingkat tidak pernah
 * jadi kotak hitam. */
function KotakResolusi({ resolusi, testid }) {
  if (!resolusi) return null;
  const cls = WARNA_SUMBER[resolusi.sumber] || WARNA_SUMBER.bawaan;
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid={testid}>
      <p className="text-xs text-slate-700">
        Uang mendarat di{" "}
        {resolusi.coa_code
          ? <><span className="font-mono font-semibold">{resolusi.coa_code}</span> · {resolusi.coa_name}</>
          : <span className="text-amber-700 font-medium">belum dipetakan</span>}
        <span className={`ml-2 px-1.5 py-0.5 rounded-full border text-[10px] ${cls}`}>{resolusi.sumber_label}</span>
      </p>
      <p className="text-[11px] text-slate-500 mt-1">{resolusi.alasan}</p>
    </div>
  );
}

const JENIS_CARDS = [
  { key: "uang_masuk",   icon: TrendingUp,     label: "Uang Masuk",   desc: "Penjualan, pelunasan piutang, setoran modal", color: "emerald" },
  { key: "uang_keluar",  icon: TrendingDown,   label: "Uang Keluar",  desc: "Belanja, bayar utang, keluar umum",           color: "rose" },
  { key: "beban",        icon: Receipt,        label: "Beban Usaha",  desc: "Sewa, listrik, gaji, marketing, dll (auto pilih beban COA)", color: "orange" },
  { key: "transfer",     icon: ArrowLeftRight, label: "Transfer",     desc: "Pindah antar rekening bank / setor tunai",    color: "sky" },
  { key: "cash_opname",  icon: Scale,          label: "Cash Opname",  desc: "Hitung fisik uang & sesuaikan saldo sistem",  color: "amber" },
];

/* localStorage prefs untuk default akun/rekening (per user browser) */
const PREF_KEY = "zoa_finance_catat_prefs";
function loadPrefs() {
  try { return JSON.parse(localStorage.getItem(PREF_KEY) || "{}"); } catch { return {}; }
}
function savePrefs(prefs) {
  try { localStorage.setItem(PREF_KEY, JSON.stringify(prefs)); } catch (e) { /* ignore quota */ }
}

export default function FinanceCatat() {
  const { jenis } = useParams();
  const nav = useNavigate();

  if (!jenis) return <Picker nav={nav} />;
  const config = JENIS_CARDS.find(j => j.key === jenis);
  if (!config) return <div className="p-8 text-rose-600">Jenis transaksi tidak dikenal.</div>;
  if (jenis === "beban") return <BebanForm config={config} onClose={() => nav("/finance")} />;
  return <Wizard jenis={jenis} config={config} onClose={() => nav("/finance")} />;
}

function Picker({ nav }) {
  const { can } = useAuth();
  const [showConfig, setShowConfig] = useState(false);
  // Layar Kelola Kategori sudah lama siap untuk jenis "uang_masuk" (backend +
  // komponen), tapi tidak pernah ada satu pun tombol yang membukanya — cuma
  // Piutang & Utang yang punya pintunya. Ini pintunya.
  const [katJenis, setKatJenis] = useState(null);
  const bolehKelolaKategori = can("finance_coa", "view");
  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-catat-picker">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Catat Transaksi</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Pilih Jenis</h1>
          <p className="text-sm text-slate-600 mt-1">Setiap transaksi otomatis mempengaruhi neraca & laba rugi.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {bolehKelolaKategori && (
            <>
              <button onClick={() => setKatJenis("uang_masuk")} data-testid="catat-kelola-kat-masuk" className="h-10 px-3 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-1.5">
                <Tag className="w-4 h-4" /> Kategori Uang Masuk
              </button>
              <button onClick={() => setKatJenis("uang_keluar")} data-testid="catat-kelola-kat-keluar" className="h-10 px-3 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-1.5">
                <Tag className="w-4 h-4" /> Kategori Uang Keluar
              </button>
            </>
          )}
          <button onClick={() => setShowConfig(true)} data-testid="catat-config" className="h-10 px-3 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
            <Settings2 className="w-4 h-4" /> Konfigurasi Default
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {JENIS_CARDS.map(j => {
          const Icon = j.icon;
          return (
            <button key={j.key} onClick={() => nav(`/finance/catat/${j.key}`)} data-testid={`catat-jenis-${j.key}`}
              className={`text-left bg-white border-2 border-slate-200 hover:border-${j.color}-400 rounded-xl p-5 shadow-sm hover:shadow-lg transition-all`}>
              <div className={`w-12 h-12 rounded-xl grid place-items-center bg-${j.color}-50 text-${j.color}-600 mb-3`}>
                <Icon className="w-6 h-6" />
              </div>
              <p className="font-display font-bold text-base text-slate-900">{j.label}</p>
              <p className="text-xs text-slate-500 mt-1">{j.desc}</p>
            </button>
          );
        })}
      </div>
      {showConfig && <ConfigPanel onClose={() => setShowConfig(false)} />}
      {katJenis && (
        <KategoriManager
          jenis={katJenis}
          title={katJenis === "uang_masuk" ? "Kelola Kategori Uang Masuk" : "Kelola Kategori Uang Keluar"}
          onClose={() => setKatJenis(null)}
        />
      )}
    </div>
  );
}

function ConfigPanel({ onClose }) {
  const [kasBank, setKasBank] = useState([]);
  const [prefs, setPrefs] = useState(loadPrefs());
  useEffect(() => { api.get("/finance/accounts/kas-bank").then(r => setKasBank(r.data || [])); }, []);
  const save = () => { savePrefs(prefs); toast.success("Preferensi tersimpan ✓"); onClose(); };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-1">Konfigurasi Default Catat Cepat</h3>
        <p className="text-xs text-slate-500 mb-4">Set rekening default untuk mempercepat input transaksi. Tersimpan di browser ini.</p>
        <div className="space-y-3">
          <Fld label="Uang Masuk ➜ default masuk ke akun mana?">
            <select value={prefs.uang_masuk_akun || ""} onChange={e => setPrefs({ ...prefs, uang_masuk_akun: e.target.value })} data-testid="cfg-masuk" className="in">
              <option value="">- Tanya setiap kali -</option>
              {kasBank.map(a => <option key={a.id} value={a.id}>{a.name} ({a.code})</option>)}
            </select>
          </Fld>
          <Fld label="Uang Keluar ➜ default keluar dari akun mana?">
            <select value={prefs.uang_keluar_akun || ""} onChange={e => setPrefs({ ...prefs, uang_keluar_akun: e.target.value })} data-testid="cfg-keluar" className="in">
              <option value="">- Tanya setiap kali -</option>
              {kasBank.map(a => <option key={a.id} value={a.id}>{a.name} ({a.code})</option>)}
            </select>
          </Fld>
          <Fld label="Beban Usaha ➜ default sumber dana?">
            <select value={prefs.beban_akun || ""} onChange={e => setPrefs({ ...prefs, beban_akun: e.target.value })} data-testid="cfg-beban" className="in">
              <option value="">- Tanya setiap kali -</option>
              {kasBank.map(a => <option key={a.id} value={a.id}>{a.name} ({a.code})</option>)}
            </select>
          </Fld>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={save} data-testid="cfg-save" className="flex-1 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium">Simpan</button>
        </div>
        <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #059669; }`}</style>
      </div>
    </div>
  );
}

/* ================ BEBAN USAHA (dedicated form) ==================== */
function BebanForm({ config, onClose }) {
  const prefs = loadPrefs();
  const [kasBank, setKasBank] = useState([]);
  const [kategoriOpts, setKategoriOpts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [f, setF] = useState({
    tanggal: hariIniISO(),
    sumber_akun_id: prefs.beban_akun || "",
    kategori: "",
    nominal: "",
    brand: "",
    deskripsi: "",
    beban_tetap: "",   // "" = belum dipilah; "tetap" | "variabel"
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/finance/accounts/kas-bank"),
      api.get("/finance/kategori-options"),
    ]).then(([a, b]) => {
      setKasBank(a.data || []);
      // Filter kategori yang mengarah ke akun beban / HPP
      const opts = (b.data.uang_keluar_options || []).filter(o => {
        const code = String(o.coa_code || "");
        return code.startsWith("6") || code.startsWith("5");
      });
      setKategoriOpts(opts);
      setBrands(b.data.brands || []);
    }).catch(() => toast.error("Gagal memuat data"));
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        tanggal: f.tanggal,
        jenis: "uang_keluar",
        kategori: f.kategori,
        nominal: Number(f.nominal),
        sumber_akun_id: f.sumber_akun_id,
        brand: f.brand || undefined,
        deskripsi: f.deskripsi || undefined,
        // Penanda BEP: backend sudah lama menerimanya, tapi UI tidak pernah
        // mengirim — nilainya selalu None dan laporan BEP tidak bisa memilah.
        beban_tetap: f.beban_tetap === "" ? undefined : f.beban_tetap === "tetap",
      };
      const r = await api.post("/finance/transactions", payload);
      toast.success(r.data.message || "Beban tercatat ✓");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setBusy(false); }
  };

  const Icon = config.icon;
  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto" data-testid="beban-form">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onClose} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="beban-cancel"><ChevronLeft className="w-4 h-4" /> Batal</button>
      </div>
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-11 h-11 rounded-xl grid place-items-center bg-orange-50 text-orange-600`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Beban Operasional</p>
          <p className="font-display font-bold text-lg text-slate-900">Catat Beban Usaha</p>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 md:p-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Tanggal"><DatePicker value={f.tanggal} onChange={v => setF({ ...f, tanggal: v })} data-testid="beban-tanggal" /></Fld>
          <Fld label="Nominal"><InputRupiah value={f.nominal} onChange={n => setF({ ...f, nominal: n })} testid="beban-nom" className="in" placeholder="0" /></Fld>
        </div>
        <Fld label="Kategori Beban">
          <select value={f.kategori} onChange={e => setF({ ...f, kategori: e.target.value })} data-testid="beban-kat" className="in">
            <option value="">Pilih kategori beban...</option>
            {kategoriOpts.map(o => <option key={o.kategori} value={o.kategori}>{o.kategori}{o.source === "custom" ? " (custom)" : ""}</option>)}
          </select>
        </Fld>
        <Fld label="Dana Keluar Dari">
          <select value={f.sumber_akun_id} onChange={e => setF({ ...f, sumber_akun_id: e.target.value })} data-testid="beban-src" className="in">
            <option value="">Pilih rekening / kas...</option>
            {kasBank.map(a => <option key={a.id} value={a.id}>{a.name} · saldo {idr(a.balance)}</option>)}
          </select>
        </Fld>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Brand (opsional)">
            <select value={f.brand} onChange={e => setF({ ...f, brand: e.target.value })} className="in">
              <option value="">-</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </Fld>
          <Fld label="Deskripsi (opsional)">
            <input value={f.deskripsi} onChange={e => setF({ ...f, deskripsi: e.target.value })} placeholder="Untuk apa..." className="in" />
          </Fld>
        </div>
        <Fld label="Sifat beban (untuk hitung titik impas / BEP)">
          <div className="flex gap-2" data-testid="beban-sifat">
            {[["", "Belum dipilah"], ["tetap", "Tetap — keluar terus walau sepi (sewa, gaji, listrik)"], ["variabel", "Variabel — ikut naik-turun penjualan"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setF({ ...f, beban_tetap: v })}
                      className={`flex-1 min-h-10 px-2 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                        f.beban_tetap === v
                          ? "border-orange-500 bg-orange-50 text-orange-800"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                {l}
              </button>
            ))}
          </div>
        </Fld>
        {f.nominal && f.kategori && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-800">
            <b>Preview:</b> Beban <span className="font-semibold">{f.kategori}</span> sebesar <span className="font-bold">{idr(Number(f.nominal))}</span>
            {f.sumber_akun_id && kasBank.find(k => k.id === f.sumber_akun_id) && <> dari <b>{kasBank.find(k => k.id === f.sumber_akun_id).name}</b></>}
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-5">
        <button onClick={onClose} className="flex-1 h-12 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium">Batal</button>
        <button onClick={submit} disabled={busy || !f.sumber_akun_id || !f.kategori || !f.nominal} data-testid="beban-submit" className="flex-1 h-12 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40"><Check className="w-5 h-5" /> {busy ? "Menyimpan..." : "Simpan Beban"}</button>
      </div>
      <style>{`.in { width: 100%; height: 40px; padding: 0 12px; border: 1px solid #E2E8F0; border-radius: 8px; font-size: 14px; outline: none; } .in:focus { border-color: #EA580C; box-shadow: 0 0 0 3px rgba(234,88,12,0.1); }`}</style>
    </div>
  );
}

/* ================ WIZARD (uang_masuk / uang_keluar / transfer / cash_opname) ==================== */
function Wizard({ jenis, config, onClose }) {
  const prefs = loadPrefs();
  const { can } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    tanggal: hariIniISO(),
    jenis,
    sumber_akun_id: jenis === "uang_masuk" ? (prefs.uang_masuk_akun || "") : (jenis === "uang_keluar" ? (prefs.uang_keluar_akun || "") : ""),
    sumber_akun_name: "",
    akun_tujuan_id: "",
    kategori: "",
    supplier_id: "",
    nominal: "",
    brand: "",
    deskripsi: "",
  });
  const [kasBank, setKasBank] = useState([]);
  const [kategoriOpts, setKategoriOpts] = useState({ brands: [], uang_masuk: [], uang_keluar: [] });
  const [suppliers, setSuppliers] = useState([]);
  const [resolusi, setResolusi] = useState(null);
  const [showKatMgr, setShowKatMgr] = useState(false);
  const [busy, setBusy] = useState(false);
  const pakaiKategori = JENIS_PAKAI_KATEGORI.includes(jenis);
  const bolehKelolaKategori = can("finance_coa", "view");

  const muatOpsi = useCallback(() => {
    Promise.all([
      api.get("/finance/accounts/kas-bank"),
      api.get("/finance/kategori-options"),
      // Daftar supplier lewat pintu Finance (data master saja) — pemegang izin
      // Catat Cepat tidak perlu diberi izin Inventory demi satu dropdown.
      // Gagal = dropdown suppliernya kosong, bukan seluruh form gagal.
      api.get("/finance/kategori-map/supplier-options").catch(() => ({ data: { items: [] } })),
    ])
      .then(([a, b, s]) => {
        setKasBank(a.data || []);
        setKategoriOpts(b.data);
        setSuppliers(s.data.items || []);
        // Set sumber_akun_name kalau prefs sudah set
        setForm(f => {
          if (!f.sumber_akun_id) return f;
          const acc = (a.data || []).find(x => x.id === f.sumber_akun_id);
          return acc ? { ...f, sumber_akun_name: acc.name } : f;
        });
      })
      .catch(() => toast.error("Gagal memuat data"));
  }, []);
  useEffect(() => { muatOpsi(); }, [muatOpsi]);

  // Pratinjau resolusi akun. Dihitung di BACKEND lewat resolver yang sama persis
  // dengan yang dipakai saat menyimpan — kalau dihitung ulang di JS, suatu hari
  // keduanya pasti berbeda dan layar akan berbohong.
  useEffect(() => {
    if (!pakaiKategori || !form.kategori) { setResolusi(null); return undefined; }
    let batal = false;
    const q = new URLSearchParams({ jenis, kategori: form.kategori });
    if (form.supplier_id) q.set("supplier_id", form.supplier_id);
    api.get(`/finance/kategori-map/resolusi?${q.toString()}`)
      .then(r => { if (!batal) setResolusi(r.data); })
      .catch(() => { if (!batal) setResolusi(null); });
    return () => { batal = true; };
  }, [jenis, pakaiKategori, form.kategori, form.supplier_id]);

  const totalSteps = jenis === "cash_opname" ? 3 : 5;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        tanggal: form.tanggal, jenis: form.jenis,
        kategori: form.kategori || (jenis === "transfer" ? "Transfer Antar Rekening" : jenis === "cash_opname" ? "Cash Opname" : ""),
        nominal: Number(form.nominal),
        sumber_akun_id: form.sumber_akun_id,
        // Backend (TransactionReq) menamai rekening tujuan `tujuan_akun_id`. Selama nama
        // yang dikirim `akun_tujuan_id`, field-nya dibuang Pydantic dan transfer SELALU
        // ditolak 400 "Pilih rekening tujuan dulu" walau user sudah memilihnya di langkah 2.
        tujuan_akun_id: jenis === "transfer" ? form.akun_tujuan_id : undefined,
        // Master Supplier (opsional). Tidak dipilih = tidak dikirim = resolusi
        // akun persis seperti sebelum pemetaan bertingkat ada.
        supplier_id: pakaiKategori && form.supplier_id ? form.supplier_id : undefined,
        brand: form.brand || undefined,
        deskripsi: form.deskripsi || undefined,
        // Penanda BEP — hanya bermakna untuk uang keluar; "" = belum dipilah (None).
        beban_tetap: jenis === "uang_keluar" && form.beban_tetap
          ? form.beban_tetap === "tetap" : undefined,
      };
      const endpoint = jenis === "cash_opname" ? "/finance/opname" : "/finance/transactions";
      const body = jenis === "cash_opname" ? { akun_id: form.sumber_akun_id, jumlah_fisik: Number(form.nominal), tanggal: form.tanggal, catatan: form.deskripsi } : payload;
      const r = await api.post(endpoint, body);
      toast.success(r.data.message || "Transaksi tercatat ✓");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setBusy(false); }
  };

  const StepIcon = config.icon;

  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto" data-testid={`wizard-${jenis}`}>
      <div className="flex items-center justify-between mb-6">
        <button onClick={onClose} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="wizard-cancel"><ChevronLeft className="w-4 h-4" /> Batal</button>
        <div className="flex items-center gap-1">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div key={i} className={`h-1.5 rounded-full transition-all ${i + 1 <= step ? `bg-${config.color}-500 w-8` : "bg-slate-200 w-4"}`} />
          ))}
        </div>
        <div className="w-16" />
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className={`w-11 h-11 rounded-xl grid place-items-center bg-${config.color}-50 text-${config.color}-600`}>
          <StepIcon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Langkah {step}/{totalSteps}</p>
          <p className="font-display font-bold text-lg text-slate-900">{config.label}</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 md:p-6 space-y-4">
        {step === 1 && (
          <>
            <Label>{jenis === "transfer" ? "Dari rekening mana?" : jenis === "uang_masuk" ? "Uang masuk ke mana?" : jenis === "cash_opname" ? "Kas mana yang mau di-opname?" : "Uang keluar dari mana?"}</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {kasBank.map(a => (
                <button key={a.id} onClick={() => setForm({ ...form, sumber_akun_id: a.id, sumber_akun_name: a.name })} data-testid={`akun-${a.code}`}
                  className={`text-left p-3 rounded-lg border-2 ${form.sumber_akun_id === a.id ? `border-${config.color}-500 bg-${config.color}-50` : "border-slate-200 hover:border-slate-300"}`}>
                  <p className="font-medium text-slate-900 text-sm">{a.name}</p>
                  <p className="text-xs text-slate-500 font-mono">{a.code} · {idr(a.balance)}</p>
                </button>
              ))}
            </div>
          </>
        )}
        {step === 2 && jenis === "transfer" && (
          <>
            <Label>Ke rekening mana?</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {kasBank.filter(a => a.id !== form.sumber_akun_id).map(a => (
                <button key={a.id} onClick={() => setForm({ ...form, akun_tujuan_id: a.id })} className={`text-left p-3 rounded-lg border-2 ${form.akun_tujuan_id === a.id ? "border-sky-500 bg-sky-50" : "border-slate-200"}`}>
                  <p className="font-medium text-slate-900 text-sm">{a.name}</p>
                  <p className="text-xs text-slate-500 font-mono">{a.code}</p>
                </button>
              ))}
            </div>
          </>
        )}
        {step === 2 && (jenis === "uang_masuk" || jenis === "uang_keluar") && (
          <>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <Label>Untuk apa?</Label>
              {bolehKelolaKategori && (
                <button onClick={() => setShowKatMgr(true)} data-testid="wizard-kelola-kategori"
                  className="h-8 px-2.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-medium inline-flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" /> Kelola Kategori
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(jenis === "uang_masuk" ? kategoriOpts.uang_masuk : kategoriOpts.uang_keluar).map(k => (
                <button key={k} onClick={() => setForm({ ...form, kategori: k })} className={`text-left p-3 rounded-lg border-2 ${form.kategori === k ? `border-${config.color}-500 bg-${config.color}-50` : "border-slate-200"}`}>
                  <p className="text-sm font-medium text-slate-900">{k}</p>
                </button>
              ))}
            </div>
            <div className="mt-4">
              <Label>Supplier (opsional)</Label>
              <select value={form.supplier_id} onChange={e => setForm({ ...form, supplier_id: e.target.value })}
                data-testid="wizard-supplier" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm">
                <option value="">- Tanpa supplier -</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.nama}</option>)}
              </select>
              <p className="text-[11px] text-slate-500 mt-1.5">
                Diisi kalau kategori ini punya pemetaan akun sendiri untuk supplier tertentu.
                Dikosongkan = memakai pemetaan kategori biasa.
              </p>
            </div>
            {form.kategori && <div className="mt-3"><KotakResolusi resolusi={resolusi} testid="wizard-resolusi" /></div>}
            {jenis === "uang_keluar" && (
              <div className="mt-4">
                <Label>Sifat beban (untuk hitung titik impas)</Label>
                <div className="flex gap-2" data-testid="wizard-beban-sifat">
                  {[["", "Belum dipilah"], ["tetap", "Tetap"], ["variabel", "Variabel"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setForm({ ...form, beban_tetap: v })}
                            className={`flex-1 h-10 rounded-lg border-2 text-sm font-medium ${
                              (form.beban_tetap || "") === v
                                ? `border-${config.color}-500 bg-${config.color}-50 text-slate-900`
                                : "border-slate-200 text-slate-600"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Tetap = keluar terus walau sepi (sewa, gaji). Variabel = ikut naik-turun penjualan.
                </p>
              </div>
            )}
          </>
        )}
        {step === 2 && jenis === "cash_opname" && (
          <>
            <Label>Berapa uang fisik di laci?</Label>
            <InputRupiah value={form.nominal} onChange={n => setForm({ ...form, nominal: n })} testid="input-nominal" className="w-full h-14 px-4 border-2 border-slate-200 rounded-xl text-2xl font-bold text-slate-900 focus:border-amber-500 outline-none" placeholder="0" />
          </>
        )}
        {step === 3 && jenis !== "cash_opname" && (
          <>
            <Label>Berapa nominalnya?</Label>
            <InputRupiah value={form.nominal} onChange={n => setForm({ ...form, nominal: n })} testid="input-nominal" className="w-full h-14 px-4 border-2 border-slate-200 rounded-xl text-2xl font-bold text-slate-900 focus:border-emerald-500 outline-none" placeholder="0" />
            <p className="text-sm text-slate-600 pt-2">{form.nominal ? idr(Number(form.nominal)) : ""}</p>
          </>
        )}
        {step === 3 && jenis === "cash_opname" && <Confirmation form={form} config={config} resolusi={resolusi} suppliers={suppliers} />}
        {step === 4 && (
          <>
            <Label>Brand & Catatan (opsional)</Label>
            <select value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm">
              <option value="">Pilih brand...</option>
              {kategoriOpts.brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <textarea value={form.deskripsi} onChange={e => setForm({ ...form, deskripsi: e.target.value })} rows={3} placeholder="Catatan (opsional)" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </>
        )}
        {step === 5 && <Confirmation form={form} config={config} resolusi={resolusi} suppliers={suppliers} />}
      </div>

      <div className="flex gap-2 mt-5">
        {step > 1 && <button onClick={() => setStep(s => s - 1)} className="flex-1 h-12 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium">← Mundur</button>}
        {step < totalSteps && <button onClick={() => setStep(s => s + 1)} disabled={!isStepValid(step, form, jenis)} data-testid="wizard-next" className={`flex-1 h-12 rounded-xl bg-${config.color}-600 hover:bg-${config.color}-700 text-white font-medium disabled:opacity-40`}>Lanjut →</button>}
        {step === totalSteps && <button onClick={submit} disabled={busy} data-testid="wizard-submit" className={`flex-1 h-12 rounded-xl bg-${config.color}-600 hover:bg-${config.color}-700 text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40`}><Check className="w-5 h-5" /> {busy ? "Menyimpan..." : "Ya Simpan"}</button>}
      </div>
      {showKatMgr && (
        <KategoriManager
          jenis={jenis}
          title={jenis === "uang_masuk" ? "Kelola Kategori Uang Masuk" : "Kelola Kategori Uang Keluar"}
          // Pilihan kategori & supplier dimuat ulang setelah dialog ditutup,
          // supaya kategori yang baru dibuat langsung bisa dipilih tanpa reload.
          onClose={() => { setShowKatMgr(false); muatOpsi(); }}
        />
      )}
    </div>
  );
}

function isStepValid(step, form, jenis) {
  if (step === 1) return !!form.sumber_akun_id;
  if (step === 2) {
    if (jenis === "transfer") return !!form.akun_tujuan_id;
    if (jenis === "cash_opname") return !!form.nominal;
    return !!form.kategori;
  }
  if (step === 3) return !!form.nominal;
  return true;
}

function Confirmation({ form, config, resolusi, suppliers }) {
  const supplierNama = (suppliers || []).find(s => s.id === form.supplier_id)?.nama;
  return (
    <div className="py-4">
      <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3 text-center">Konfirmasi</p>
      <p className="text-slate-700 leading-relaxed text-center">
        Kamu mencatat <span className={`text-${config.color}-700 font-semibold`}>{config.label}</span> {form.nominal && <span className="font-bold text-slate-900">{idr(Number(form.nominal))}</span>} {form.sumber_akun_name && <>dari <span className="font-semibold">{form.sumber_akun_name}</span></>}{form.kategori && <> untuk <span className="font-semibold">{form.kategori}</span></>}.
        {supplierNama && <> Supplier: <span className="font-semibold">{supplierNama}</span>.</>}
        {form.brand && <> Brand: {form.brand}.</>}
        {form.deskripsi && <> Catatan: &quot;{form.deskripsi}&quot;.</>}
      </p>
      {/* Akun tujuan + alasannya ditampilkan SEBELUM tombol simpan — kalau
          pemetaannya salah, di sinilah satu-satunya kesempatan menangkapnya. */}
      {resolusi && <div className="mt-3"><KotakResolusi resolusi={resolusi} testid="konfirmasi-resolusi" /></div>}
      <p className="text-xs text-slate-500 mt-3 text-center">Sudah benar? Klik <b>Ya Simpan</b>.</p>
    </div>
  );
}

function Label({ children }) {
  return <p className="text-sm font-medium text-slate-900 mb-3">{children}</p>;
}

function Fld({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
